import React, { useCallback, useEffect, useRef, useState } from "react";
import { PortalService } from "services/portal";
import { Upload, FileText, Trash2, ExternalLink, Loader2, CheckCircle, AlertCircle, Clock } from "lucide-react";

const CARD = { background: "var(--bg-card)", borderRadius: 12, border: "1px solid var(--border)", padding: 16 };

const STATUS_BADGE = {
  PENDING:    { bg: "#fef3c7", color: "#92400e", label: "Sin clasificar",  Icon: Clock },
  CLASSIFIED: { bg: "#dbeafe", color: "#1e40af", label: "Pre-clasificado", Icon: AlertCircle },
  POSTED:     { bg: "#dcfce7", color: "#166534", label: "Aprobado",        Icon: CheckCircle },
  REJECTED:   { bg: "#fee2e2", color: "#991b1b", label: "Rechazado",       Icon: AlertCircle },
};

const ACCEPTED_TYPES = "application/pdf,image/jpeg,image/png,image/webp";

function fmtCLP(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n);
}

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.PENDING;
  const Icon = s.Icon;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px",
      borderRadius: 999, background: s.bg, color: s.color, fontSize: 11, fontWeight: 600,
    }}>
      <Icon size={11} /> {s.label}
    </span>
  );
}

export function PortalGastos() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]); // {id, name, status, progress, error}
  const [selectedBill, setSelectedBill] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const loadBills = useCallback(async () => {
    setLoading(true);
    try {
      const data = await PortalService.listExpenses({ limit: 200 });
      setBills(data.bills || []);
    } catch (err) {
      console.error("listExpenses:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBills(); }, [loadBills]);

  const handleFiles = useCallback(async (files) => {
    const fileList = Array.from(files || []);
    if (fileList.length === 0) return;

    for (const file of fileList) {
      const id = `${Date.now()}-${file.name}`;
      setUploads((u) => [...u, { id, name: file.name, status: "uploading", progress: 0 }]);
      try {
        const res = await PortalService.uploadExpense(file, (p) =>
          setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress: p } : x)))
        );
        setUploads((u) => u.map((x) => (x.id === id ? {
          ...x, status: "done", progress: 100,
          aiOk: res.aiOk,
          aiError: res.aiError,
        } : x)));
      } catch (err) {
        setUploads((u) => u.map((x) => (x.id === id ? { ...x, status: "error", error: err.message } : x)));
      }
    }
    // Refrescar la lista al final
    await loadBills();
    // Limpiar uploads "done" tras 4 segundos
    setTimeout(() => setUploads((u) => u.filter((x) => x.status !== "done")), 4000);
  }, [loadBills]);

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };
  const handleDragOver = (e) => e.preventDefault();

  const openBill = async (bill) => {
    setSelectedBill(bill);
    setPreviewUrl(null);
    try {
      const { url } = await PortalService.getExpenseViewUrl(bill._id);
      setPreviewUrl(url);
    } catch (err) {
      console.error("getExpenseViewUrl:", err);
    }
  };

  const closeModal = () => { setSelectedBill(null); setPreviewUrl(null); };

  const handleDelete = async (bill) => {
    if (!window.confirm(`¿Eliminar "${bill.file?.name || "este gasto"}"? Esta acción no se puede deshacer.`)) return;
    try {
      await PortalService.deleteExpense(bill._id);
      setSelectedBill(null);
      setPreviewUrl(null);
      await loadBills();
    } catch (err) {
      alert(`No se pudo eliminar: ${err?.response?.data?.error || err.message}`);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Mis Gastos</h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)" }}>
          Sube tus boletas y facturas. La IA las pre-clasifica y el contador las aprueba para registrarlas en tu libro diario.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
        style={{
          ...CARD,
          border: "2px dashed var(--border)",
          padding: 32,
          textAlign: "center",
          cursor: "pointer",
          marginBottom: 16,
          transition: "all 0.15s",
        }}
      >
        <Upload size={32} color="#94a3b8" style={{ margin: "0 auto 8px" }} />
        <p style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>
          Arrastra una boleta o factura aquí
        </p>
        <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
          o haz click para seleccionar — PDF, JPG, PNG, WebP (máx 10MB)
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {/* Uploads en curso */}
      {uploads.length > 0 && (
        <div style={{ ...CARD, marginBottom: 16 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 8 }}>Subidas</h3>
          {uploads.map((u) => (
            <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", fontSize: 13 }}>
              {u.status === "uploading" ? <Loader2 size={14} className="spin" color="#0353a4" /> :
               u.status === "done" ? <CheckCircle size={14} color="#16a34a" /> :
               <AlertCircle size={14} color="#dc2626" />}
              <span style={{ flex: 1 }}>{u.name}</span>
              {u.status === "uploading" && <span style={{ color: "#0353a4", fontSize: 12 }}>{u.progress}%</span>}
              {u.status === "error" && <span style={{ color: "#dc2626", fontSize: 12 }}>{u.error}</span>}
              {u.status === "done" && (
                <span style={{ color: u.aiOk ? "#16a34a" : "#92400e", fontSize: 12 }} title={u.aiError || ""}>
                  {u.aiOk ? "Clasificado por IA" : `Sin clasificar${u.aiError ? ` (${u.aiError.slice(0, 40)}…)` : ""}`}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lista de bills */}
      <div style={CARD}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Historial</h3>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{bills.length} gasto(s)</span>
        </div>

        {loading ? (
          <p style={{ color: "var(--text-muted)", fontSize: 13, padding: "16px 0", textAlign: "center" }}>Cargando...</p>
        ) : bills.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontSize: 13, padding: "16px 0", textAlign: "center" }}>
            Aún no has subido ningún gasto.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {bills.map((b) => (
              <div
                key={b._id}
                onClick={() => openBill(b)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto auto auto",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-elevated)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <FileText size={18} color="#64748b" />
                <div style={{ overflow: "hidden" }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {b.supplier?.name || b.file?.name || "(sin proveedor)"}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    {b.date ? new Date(b.date).toLocaleDateString("es-CL") : "—"}
                    {b.accountCode && ` · ${b.accountCode}`}
                    {b.classifiedBy === "AUTO" && " · 🤖 IA"}
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{fmtCLP(b.amount)}</div>
                <StatusBadge status={b.status} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal preview */}
      {selectedBill && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 12, maxWidth: 900, width: "100%",
              maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden",
            }}
          >
            <div style={{ padding: 16, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 600 }}>
                  {selectedBill.supplier?.name || selectedBill.file?.name || "Gasto"}
                </h2>
                <div style={{ display: "flex", gap: 8, marginTop: 4, alignItems: "center" }}>
                  <StatusBadge status={selectedBill.status} />
                  {selectedBill.classifiedBy === "AUTO" && (
                    <span style={{ fontSize: 11, color: "#64748b" }}>
                      🤖 Sugerencia IA: {selectedBill.accountCode}
                      {selectedBill.extracted?.confidence != null && ` (${Math.round(selectedBill.extracted.confidence * 100)}%)`}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={closeModal} style={{ background: "transparent", border: "none", fontSize: 24, cursor: "pointer", color: "#64748b" }}>×</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", flex: 1, overflow: "hidden" }}>
              {/* Preview */}
              <div style={{ background: "#f8fafc", overflow: "auto" }}>
                {previewUrl ? (
                  selectedBill.file?.contentType?.startsWith("image/") ? (
                    <img src={previewUrl} alt={selectedBill.file?.name} style={{ width: "100%", height: "auto" }} />
                  ) : (
                    <iframe src={previewUrl} title="preview" style={{ width: "100%", height: "100%", border: "none" }} />
                  )
                ) : (
                  <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>Cargando preview...</div>
                )}
              </div>

              {/* Detalle */}
              <div style={{ padding: 16, borderLeft: "1px solid #e5e7eb", overflow: "auto" }}>
                <Field label="Proveedor" value={selectedBill.supplier?.name} />
                <Field label="RUT" value={selectedBill.supplier?.rut} />
                <Field label="Tipo" value={selectedBill.type} />
                <Field label="Fecha" value={selectedBill.date ? new Date(selectedBill.date).toLocaleDateString("es-CL") : "—"} />
                <Field label="Monto" value={fmtCLP(selectedBill.amount)} />
                <Field label="Neto" value={fmtCLP(selectedBill.neto)} />
                <Field label="IVA" value={fmtCLP(selectedBill.iva)} />
                <Field label="Cuenta" value={selectedBill.accountCode || "—"} />
                <Field label="Descripción" value={selectedBill.description || "—"} />

                {selectedBill.extracted?.reason && (
                  <div style={{ marginTop: 12, padding: 8, background: "#eff6ff", borderRadius: 6, fontSize: 11, color: "#1e40af" }}>
                    <strong>IA:</strong> {selectedBill.extracted.reason}
                  </div>
                )}

                {/* Estado terminal */}
                {selectedBill.status === "POSTED" && (
                  <div style={{ marginTop: 12, padding: 10, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 6, fontSize: 12, color: "#166534" }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      <CheckCircle size={12} style={{ verticalAlign: "middle" }} /> Aprobado por tu contador
                    </div>
                    {selectedBill.approvedAt && (
                      <div style={{ fontSize: 11 }}>
                        {new Date(selectedBill.approvedAt).toLocaleString("es-CL")}
                      </div>
                    )}
                    <div style={{ fontSize: 11, marginTop: 4, paddingTop: 4, borderTop: "1px dashed #86efac" }}>
                      Registrado en libro diario con cuenta <code>{selectedBill.accountCode}</code>
                    </div>
                  </div>
                )}
                {selectedBill.status === "REJECTED" && (
                  <div style={{ marginTop: 12, padding: 10, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, fontSize: 12, color: "#991b1b" }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      <AlertCircle size={12} style={{ verticalAlign: "middle" }} /> Rechazado
                    </div>
                    {selectedBill.rejectedReason && (
                      <div style={{ fontStyle: "italic", fontSize: 11 }}>{selectedBill.rejectedReason}</div>
                    )}
                  </div>
                )}

                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  {previewUrl && (
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{
                      display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 10px",
                      border: "1px solid #e2e8f0", borderRadius: 6, color: "#334155", fontSize: 12,
                      textDecoration: "none",
                    }}>
                      <ExternalLink size={12} /> Abrir
                    </a>
                  )}
                  {selectedBill.status !== "POSTED" && (
                    <button onClick={() => handleDelete(selectedBill)} style={{
                      display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 10px",
                      border: "1px solid #fca5a5", borderRadius: 6, background: "#fff", color: "#dc2626",
                      fontSize: 12, cursor: "pointer",
                    }}>
                      <Trash2 size={12} /> Eliminar
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--text-primary)" }}>{value || "—"}</div>
    </div>
  );
}
